import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Checkbox,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { formatJerseyNumber, sanitizeJerseyNumber } from '../../utils/formatUtils';
import {
  Healing as HealingIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import PlayerSearchAutocomplete from '../MatchReport/PlayerSearchAutocomplete.tsx';
import InjuryHistoryDialog from './InjuryHistoryDialog';

const POSITION_OPTIONS = ['GK', 'FB', 'MF', 'FW'];  

const TeamRosterManage = ({ open, onClose, teamId, teamName, onUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionPlayerId, setActionPlayerId] = useState(null);
  const [addingSpare, setAddingSpare] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editJerseyNumber, setEditJerseyNumber] = useState('');
  const [editPosition, setEditPosition] = useState('');
  const [injuryDialogOpen, setInjuryDialogOpen] = useState(false);
  const [selectedPlayerForInjury, setSelectedPlayerForInjury] = useState(null);
  const [teamRoleIds, setTeamRoleIds] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [selectedImportRows, setSelectedImportRows] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const hasChangesRef = useRef(false);

  const loadTeamRoleIds = async () => {
    const roles = await unifiedApi.get('/api/UserTeam/roles');
    const roleIds = (Array.isArray(roles) ? roles : []).reduce((ids, role) => ({
      ...ids,
      [role.name]: role.id
    }), {});
    setTeamRoleIds(roleIds);
    return roleIds;
  };

  const loadPlayers = async (forceRefresh = false) => {
    if (!teamId) return;

    setLoading(true);
    setError('');
    try {
      // Add timestamp to bust the unifiedApi 5-second GET cache when refreshing after a change
      const url = forceRefresh
        ? `/api/UserTeam/team/${teamId}/players?_t=${Date.now()}`
        : `/api/UserTeam/team/${teamId}/players`;
      const data = await unifiedApi.get(url);
      setPlayers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load team players with details:', err);
      setError('Failed to load team roster');
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPlayers();
      loadTeamRoleIds().catch((err) => {
        console.error('Failed to load team roles:', err);
        setError('Failed to load team roles');
      });
    } else {
      setPlayers([]);
      setError('');
      setTeamRoleIds({});
      setAddingSpare(false);
      setActionPlayerId(null);
      setEditingPlayerId(null);
      setEditJerseyNumber('');
      setEditPosition('');
      setImportFile(null);
      setImportPreview(null);
      setSelectedImportRows({});
      setImportResult(null);
      setConfirmImportOpen(false);
      hasChangesRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId]);

  const handleAfterChange = async () => {
    hasChangesRef.current = true;
    await loadPlayers(true);
  };

  const handleViewInjuryHistory = (player) => {
    setSelectedPlayerForInjury(player);
    setInjuryDialogOpen(true);
  };

  const handleMarkInjured = async (player) => {
    if (!teamId || !player?.id) return;

    setActionPlayerId(player.id);
    setError('');
    try {
      await unifiedApi.post(`/api/UserTeam/${player.id}/${teamId}/injury/start`, {});
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to mark player injured:', err);
      setError('Failed to mark player as injured');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleClearInjury = async (player) => {
    if (!teamId || !player?.id) return;

    setActionPlayerId(player.id);
    setError('');
    try {
      await unifiedApi.post(`/api/UserTeam/${player.id}/${teamId}/injury/end`, {});
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to clear player injury:', err);
      setError('Failed to clear player injury');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleToggleMembershipType = async (player) => {
    if (!teamId || !player?.id) return;

    const currentType = player.membershipType || 'Regular';
    const newType = currentType === 'LongTermSpare' ? 'Regular' : 'LongTermSpare';

    setActionPlayerId(player.id);
    setError('');
    try {
      const payload = {
        id: 0,
        userTenantId: player.id,
        email: player.email || '',
        firstName: null,
        lastName: null,
        teamId: player.teamId,
        userTeamRoleId: 0,
        jerseyNumber: player.jerseyNumber || null,
        position: player.position || null,
        membershipType: newType
      };

      await unifiedApi.put(`/api/UserTeam/${player.id}/${teamId}`, payload);
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to update membership type:', err);
      setError('Failed to update player membership');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleRemoveFromTeam = async (player) => {
    if (!teamId || !player?.id) return;

    if (!window.confirm(`Remove ${player.fullName || player.firstName || 'this player'} from ${teamName || 'this team'}?`)) {
      return;
    }

    setActionPlayerId(player.id);
    setError('');
    try {
      await unifiedApi.delete(`/api/UserTeam/${player.id}/${teamId}`);
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to remove player from team:', err);
      setError('Failed to remove player from team');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleAddSpareFromSearch = async (player) => {
    if (!teamId || !player?.userTenantId || addingSpare) return;

    setAddingSpare(true);
    setError('');
    try {
      const payload = {
        email: player.email,
        firstName: player.firstName || null,
        lastName: player.lastName || null,
        teamId,
        userTeamRoleId: 0,
        jerseyNumber: null,
        position: null,
        membershipType: 'LongTermSpare'
      };

      await unifiedApi.post('/api/UserTeam', payload);
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to add spare player:', err);
      setError('Failed to add spare player');
    } finally {
      setAddingSpare(false);
    }
  };

  const handleStartEdit = (player) => {
    setEditingPlayerId(player.id);
    setEditJerseyNumber(player.jerseyNumber || '');
    setEditPosition(player.position || '');
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setEditJerseyNumber('');
    setEditPosition('');
  };

  const handleSaveEdit = async (player) => {
    if (!teamId || !player?.id) return;

    setActionPlayerId(player.id);
    setError('');
    try {
      const payload = {
        id: 0,
        userTenantId: player.id,
        email: player.email || '',
        firstName: null,
        lastName: null,
        teamId: player.teamId,
        userTeamRoleId: 0,
        jerseyNumber: editJerseyNumber.trim() || null,
        position: editPosition || null,
        membershipType: player.membershipType || 'Regular'
      };

      await unifiedApi.put(`/api/UserTeam/${player.id}/${teamId}`, payload);
      setEditingPlayerId(null);
      setEditJerseyNumber('');
      setEditPosition('');
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to update player details:', err);
      setError('Failed to update player details');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleToggleManager = async (player) => {
    if (!teamId || !player?.id) return;

    setActionPlayerId(player.id);
    setError('');
    try {
      const isCurrentlyManager = player.teamRole === 'team-manager';
      const targetRoleName = isCurrentlyManager ? 'player' : 'team-manager';
      const roleIds = teamRoleIds[targetRoleName] ? teamRoleIds : await loadTeamRoleIds();
      const newRoleId = roleIds[targetRoleName];

      if (!newRoleId) {
        throw new Error(`Unable to find ${targetRoleName} role`);
      }

      const payload = {
        id: 0,
        userTenantId: player.id,
        email: player.email || '',
        firstName: null,
        lastName: null,
        teamId: player.teamId,
        userTeamRoleId: newRoleId,
        jerseyNumber: player.jerseyNumber || null,
        position: player.position || null,
        membershipType: player.membershipType || 'Regular'
      };

      await unifiedApi.put(`/api/UserTeam/${player.id}/${teamId}`, payload);
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to update player role:', err);
      setError(err.message || 'Failed to update player role');
    } finally {
      setActionPlayerId(null);
    }
  };

  const handleClose = () => {
    if (hasChangesRef.current && onUpdated) {
      onUpdated();
      hasChangesRef.current = false;
    }
    onClose();
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImportFile(file);
    setImportPreview(null);
    setSelectedImportRows({});
    setImportResult(null);
  };

  const handlePreviewImport = async () => {
    if (!teamId || !importFile) return;

    setImporting(true);
    setError('');
    setImportResult(null);
    try {
      const preview = await unifiedApi.previewTeamRosterImport(teamId, importFile);
      setImportPreview(preview);
      const selectedRows = {};
      (preview?.rows || []).forEach((row) => {
        if (row.isSelectable && row.isSelectedByDefault) {
          selectedRows[row.rowNumber] = true;
        }
      });
      setSelectedImportRows(selectedRows);
    } catch (err) {
      console.error('Failed to preview roster import:', err);
      setError('Failed to preview roster import');
    } finally {
      setImporting(false);
    }
  };

  const handleToggleImportRow = (row) => {
    if (!row.isSelectable) return;
    setSelectedImportRows((current) => ({
      ...current,
      [row.rowNumber]: !current[row.rowNumber]
    }));
  };

  const getSelectedImportRows = () => (importPreview?.rows || [])
    .filter((row) => row.isSelectable && selectedImportRows[row.rowNumber]);

  const handlePublishImport = async () => {
    const rowsToPublish = getSelectedImportRows().map((row) => ({
      rowNumber: row.rowNumber,
      userTenantId: row.userTenantId,
      inputName: row.inputName,
      position: row.position || null,
      jerseyNumber: row.jerseyNumber || null
    }));

    if (!teamId || rowsToPublish.length === 0) return;

    setImporting(true);
    setError('');
    try {
      const result = await unifiedApi.publishTeamRosterImport(teamId, rowsToPublish);
      setImportResult(result);
      setConfirmImportOpen(false);
      hasChangesRef.current = true;
      await loadPlayers(true);
    } catch (err) {
      console.error('Failed to publish roster import:', err);
      setError('Failed to publish roster import');
    } finally {
      setImporting(false);
    }
  };

  const getImportStatusColor = (status) => {
    if (status === 'ExactMatch' || status === 'FuzzyMatch') return 'success';
    if (status === 'AlreadyOnTeam') return 'info';
    if (status === 'Ambiguous') return 'warning';
    return 'error';
  };

  const getImportStatusLabel = (status) => {
    switch (status) {
      case 'ExactMatch': return 'Exact';
      case 'FuzzyMatch': return 'Fuzzy';
      case 'AlreadyOnTeam': return 'Already on team';
      case 'InvalidPosition': return 'Invalid position';
      case 'NotEligible': return 'Not in league';
      case 'Ambiguous': return 'Ambiguous';
      case 'Unmatched': return 'Unmatched';
      default: return status || 'Invalid';
    }
  };

  const getStatusChipColor = (status) => {
    if (status === 'Injured') return 'error';
    return 'success';
  };

  const getStatusLabel = (status) => {
    // Handle null, undefined, empty, or integer values
    if (!status || status === 0 || status === '0' || status === '') {
      return 'Active';
    }
    if (status === 'Injured' || status === 2 || status === '2') {
      return 'Injured';
    }
    return status;
  };

  const getMembershipLabel = (membershipType) => {
    if (membershipType === 'LongTermSpare') return 'Spare';
    return 'Regular';
  };

  const renderImportStatus = (row) => (
    <Stack spacing={0.5}>
      <Chip
        label={getImportStatusLabel(row.status)}
        size="small"
        color={getImportStatusColor(row.status)}
        clickable={false}
        onClick={() => {}}
      />
      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
        {row.message}
      </Typography>
    </Stack>
  );

  const renderImportMatchedPlayer = (row) => (
    <Box>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{row.matchedName || '-'}</Typography>
      {row.email && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', display: 'block' }}>{row.email}</Typography>
      )}
      {row.candidates?.length > 0 && !row.matchedName && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
          {row.candidates.map((candidate) => `${candidate.fullName} (${candidate.matchScore}%)`).join(', ')}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit Roster{teamName ? ` ${teamName}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage injuries, spare status, and team membership
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Add Temporary Spare
            </Typography>

            <Box>
              <PlayerSearchAutocomplete
                onPlayerSelected={handleAddSpareFromSearch}
                label="Search and add spare player"
                placeholder="Type player name to search..."
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Search for existing players in your organization to add them as a spare.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Import Roster CSV
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Upload player names and positions, review matches, then confirm selected additions.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth={false}
                >
                  Choose CSV
                  <input hidden type="file" accept=".csv,text/csv" aria-label="Roster CSV file" onChange={handleImportFileChange} />
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePreviewImport}
                  disabled={!importFile || importing}
                >
                  Preview
                </Button>
              </Stack>
            </Stack>

            {importFile && (
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {importFile.name}
              </Typography>
            )}

            {importPreview?.errors?.length > 0 && (
              <Alert severity="error">
                {importPreview.errors.join(' ')}
              </Alert>
            )}

            {importResult && (
              <Alert severity={importResult.success === false ? 'warning' : 'success'}>
                Added {importResult.addedCount || 0} player{(importResult.addedCount || 0) === 1 ? '' : 's'}; skipped {importResult.skippedCount || 0}.
              </Alert>
            )}

            {importPreview?.rows?.length > 0 && (
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {getSelectedImportRows().length} selected of {importPreview.addableRows || 0} addable rows
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmImportOpen(true)}
                    disabled={getSelectedImportRows().length === 0 || importing}
                  >
                    Publish Selected
                  </Button>
                </Stack>

                {isMobile ? (
                  <Stack spacing={1}>
                    {importPreview.rows.map((row) => (
                      <Paper key={row.rowNumber} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Checkbox
                              checked={Boolean(selectedImportRows[row.rowNumber])}
                              onChange={() => handleToggleImportRow(row)}
                              disabled={!row.isSelectable || importing}
                              inputProps={{ 'aria-label': `Select row ${row.rowNumber}` }}
                              sx={{ p: 0.5 }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{row.inputName || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">Row {row.rowNumber}</Typography>
                            </Box>
                          </Stack>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Matched Player</Typography>
                            {renderImportMatchedPlayer(row)}
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Position</Typography>
                            <Typography variant="body2">{row.position || '-'}</Typography>
                          </Box>
                          {renderImportStatus(row)}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">Add</TableCell>
                          <TableCell>CSV Name</TableCell>
                          <TableCell>Matched Player</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importPreview.rows.map((row) => (
                          <TableRow key={row.rowNumber} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={Boolean(selectedImportRows[row.rowNumber])}
                                onChange={() => handleToggleImportRow(row)}
                                disabled={!row.isSelectable || importing}
                                inputProps={{ 'aria-label': `Select row ${row.rowNumber}` }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{row.inputName || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">Row {row.rowNumber}</Typography>
                            </TableCell>
                            <TableCell>{renderImportMatchedPlayer(row)}</TableCell>
                            <TableCell>{row.position || '-'}</TableCell>
                            <TableCell>{renderImportStatus(row)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Stack>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : players.length === 0 ? (
          <Alert severity="info">No players found for this team.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>Jersey #</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Membership</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {players.map((player) => (
                  <TableRow key={player.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim()}
                        </Typography>
                        {player.teamRole === 'team-manager' && (
                          <Chip
                            label="Manager"
                            size="small"
                            color="primary"
                            icon={<StarIcon sx={{ fontSize: 16 }} />}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                            onClick={() => {/* do nothing */}}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {player.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {editingPlayerId === player.id ? (
                        <TextField
                          size="small"
                          value={editJerseyNumber}
                          onChange={(e) => setEditJerseyNumber(sanitizeJerseyNumber(e.target.value))}
                          sx={{ width: 80 }}
                          inputProps={{ maxLength: 5 }}
                        />
                      ) : (
                        formatJerseyNumber(player.jerseyNumber) || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {editingPlayerId === player.id ? (
                        <Select
                          size="small"
                          value={editPosition}
                          onChange={(e) => setEditPosition(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {POSITION_OPTIONS.map(pos => (
                            <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        player.position || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(player.playerStatus)}
                        size="small"
                        color={getStatusChipColor(player.playerStatus)}
                        onClick={() => handleViewInjuryHistory(player)}
                        clickable
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getMembershipLabel(player.membershipType)}
                        size="small"
                        variant="outlined"
                        onClick={() => {/* do nothing */}}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {editingPlayerId === player.id ? (
                          <>
                            <Tooltip title="Save">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveEdit(player)}
                                  disabled={actionPlayerId === player.id}
                                >
                                  <SaveIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={actionPlayerId === player.id}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Edit jersey / position">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleStartEdit(player)}
                                disabled={actionPlayerId === player.id || editingPlayerId !== null}
                              >
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {editingPlayerId !== player.id && (
                          <>
                            {player.playerStatus === 'Injured' ? (
                              <Tooltip title="Clear injury">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleClearInjury(player)}
                                    disabled={actionPlayerId === player.id}
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Mark injured">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleMarkInjured(player)}
                                    disabled={actionPlayerId === player.id}
                                  >
                                    <HealingIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={player.membershipType === 'LongTermSpare' ? 'Set Regular member' : 'Make Long-term Spare'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleMembershipType(player)}
                                  disabled={actionPlayerId === player.id}
                                >
                                  <SwapIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={player.teamRole === 'team-manager' ? 'Remove Manager role' : 'Make Team Manager'}>
                              <span>
                                <IconButton
                                  aria-label={player.teamRole === 'team-manager' ? 'Remove Manager role' : 'Make Team Manager'}
                                  size="small"
                                  color={player.teamRole === 'team-manager' ? 'primary' : 'default'}
                                  onClick={() => handleToggleManager(player)}
                                  disabled={actionPlayerId === player.id}
                                >
                                  {player.teamRole === 'team-manager' ? <StarIcon /> : <StarBorderIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove from team">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleRemoveFromTeam(player)}
                                  disabled={actionPlayerId === player.id}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      <Dialog open={confirmImportOpen} onClose={() => setConfirmImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Roster Import</DialogTitle>
        <DialogContent>
          <Typography>
            Add {getSelectedImportRows().length} selected player{getSelectedImportRows().length === 1 ? '' : 's'} to {teamName || 'this team'}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Existing team members and unchecked rows will be skipped.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmImportOpen(false)} disabled={importing}>Cancel</Button>
          <Button onClick={handlePublishImport} variant="contained" disabled={importing || getSelectedImportRows().length === 0}>
            {importing ? 'Publishing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <InjuryHistoryDialog
        open={injuryDialogOpen}
        onClose={() => setInjuryDialogOpen(false)}
        player={selectedPlayerForInjury}
        teamId={teamId}
        onUpdated={handleAfterChange}
      />
    </Dialog>
  );
};

export default TeamRosterManage;
